import firebase, { firestore } from 'firebase';
import { ToDo, TaskName } from '@/models/todo';
import { GrowthCycle } from '@/models/growth-cycle';
import moment from 'moment';
import { Moment } from 'moment';
import { GrowthCycleTemplate } from '@/models/growth-cycle-template';
import store from '@/store';
import { Farm } from '@/models/farm';

export default class TodoService {
    private todosRef = firebase.firestore().collection('todos');
    private templatesRef = firebase.firestore().collection('growthCycleTemplates');
    private growthCyclesRef = firebase.firestore().collection('growthCycles');

    public completeTodo(id: string): void {
        const toDo = this.todosRef.doc(id) as firestore.DocumentReference;

        toDo.update({
            completed: true,
        });

        // Calculate the next todo
        toDo.get().then((value: any) => {
            const thisTodo = value.data() as ToDo;

            const currentRafts = thisTodo.rafts;
            const currentCellsRemaining = thisTodo.cells;
            // Maybe for later use
            // const currentRaftType = thisTodo.raftType;

            const growthCycleId = thisTodo.growthCycleId as unknown as firestore.DocumentReference;

            this.completeGrowthCycleStep(thisTodo.taskName, growthCycleId, currentRafts, currentCellsRemaining);

            // If we've harvested, no need to create another ToDo
            if (thisTodo.taskName !== TaskName.HARVEST) {
                const growthCycleTemplateId = thisTodo.growthCycleTemplateId as unknown as firestore.DocumentReference;
                const today = moment();
                let nextTaskName: TaskName;
                let dueDate: Moment;

                let rafts: number|undefined;
                let cells: number|undefined;

                growthCycleTemplateId.get().then((templateValue) => {
                    const template = templateValue.data();

                    switch (thisTodo.taskName) {
                        case TaskName.START_GERMINATION:
                            nextTaskName = TaskName.MOVE_TO_PROPAGATION;
                            dueDate = today.add(template!!.daysInGermination, 'days');
                            rafts = 0;
                            cells = thisTodo.cells;
                            break;
                        case TaskName.MOVE_TO_PROPAGATION:
                            nextTaskName = TaskName.MOVE_TO_HIGH_DENSITY;
                            dueDate = today.add(template!!.daysInPropagation, 'days');
                            rafts = template!!.expectedHighDensityRafts || thisTodo.rafts;
                            cells = template!!.expectedHighDensityCellsRemaining || thisTodo.cells;
                            break;
                         case TaskName.MOVE_TO_HIGH_DENSITY:
                            nextTaskName = TaskName.MOVE_TO_FINISHED_SPACING;
                            dueDate = today.add(template!!.daysInHighDensity, 'days');
                            rafts = template!!.expectedFinishedSpacingRafts || thisTodo.rafts;
                            cells = template!!.expectedFinishedSpacingCellsRemaining || thisTodo.cells;
                            break;
                        case TaskName.MOVE_TO_FINISHED_SPACING:
                            nextTaskName = TaskName.HARVEST;
                            dueDate = today.add(template!!.daysInFinishedSpacing, 'days');
                            rafts = template!!.expectedFinishedSpacingRafts || thisTodo.rafts;
                            cells = template!!.expectedFinishedSpacingCellsRemaining || thisTodo.cells;
                            break;
                    }

                    this.addTodo(
                        nextTaskName,
                        dueDate,
                        growthCycleId.id,
                        growthCycleTemplateId.id,
                        thisTodo.varietyName,
                        thisTodo.farmId,
                        rafts,
                        cells,
                        thisTodo.harvestInterval,
                    );
                });
           } else {
                store.dispatch('getIncompleteTodos');
           }
        });
    }

    public addFirstTodo(growthCycleRef: firestore.DocumentReference) {
        growthCycleRef.get().then((snapshot) => {

            const growthCycleId = snapshot.id;
            const growthCycle = snapshot.data() as GrowthCycle;
            const farmId = growthCycle.farmId;
            const growthCycleTemplateRef = growthCycle.growthCycleTemplateId || '' as any;

            const today = moment();
            const cells = growthCycle.germinationCells;

            let taskName: TaskName;
            let dueDate: Moment;

            if (growthCycleTemplateRef) {
                this.templatesRef.doc(growthCycleTemplateRef.id).get().then((templateSnapshot) => {
                    const growthCycleTemplate = templateSnapshot.data() as GrowthCycleTemplate;

                    if (growthCycle.germinationStartedAt) {
                        taskName = TaskName.MOVE_TO_PROPAGATION;
                        dueDate = today.add(growthCycleTemplate.daysInGermination, 'days');
                    } else {
                        taskName = TaskName.START_GERMINATION;
                        dueDate = today;
                    }
                    this.addTodo(
                        taskName,
                        dueDate,
                        growthCycleId,
                        growthCycleTemplateRef.id,
                        growthCycle.varietyName,
                        farmId,
                        0,
                        cells,
                        growthCycleTemplate.harvestInterval);
                });
            } else {
                // If we don't have a template, we can't make a good guess at how long each stage lasts
                dueDate = today;
                taskName = (growthCycle.germinationStartedAt)
                    ? TaskName.MOVE_TO_PROPAGATION
                    : TaskName.START_GERMINATION;
                this.addTodo(
                    taskName,
                    dueDate,
                    growthCycleId,
                    growthCycleTemplateRef.id,
                    growthCycle.varietyName,
                    farmId,
                    0,
                    cells);
            }
        });
    }

    public addTodo(
        taskName: TaskName,
        dueDate: Moment,
        growthCycleId: string,
        growthCycleTemplateId: string,
        varietyName: string,
        farmId: Farm,
        rafts: number = 0,
        cells: number = 0,
        harvestInterval = 0,
        ): void {
            this.todosRef.add({
                growthCycleId: this.growthCyclesRef.doc(growthCycleId),
                growthCycleTemplateId: this.templatesRef.doc(growthCycleTemplateId),
                taskName,
                dueDate: new Date(dueDate.format('MM/DD/YYYY')),
                completed: false,
                varietyName,
                farmId,
                rafts,
                cells,
                harvestInterval,
            }).then((val) => {
                store.dispatch('getIncompleteTodos');
            });
    }

    private completeGrowthCycleStep(
        taskName: TaskName,
        growthCycleId: firestore.DocumentReference,
        rafts: number,
        cellsRemaining: number) {
        const growthCycle = growthCycleId;
        let updateParams = {};
        const today = new Date();

        switch (taskName) {
            case TaskName.START_GERMINATION:
                updateParams = {
                    germinationStartedAt: today,
                    germinationCells: cellsRemaining,
                };
                break;
            case TaskName.MOVE_TO_PROPAGATION:
                updateParams = {
                    propagationStartedAt: today,
                };
                break;
            case TaskName.MOVE_TO_HIGH_DENSITY:
                updateParams = {
                    highDensityStartedAt: today,
                    highDensityRafts: rafts,
                    highDensityCellsRemaining: cellsRemaining,
                };
                break;
            case TaskName.MOVE_TO_FINISHED_SPACING:
                updateParams = {
                    finishedSpacingStartedAt: today,
                    finishedSpacingRafts: rafts,
                    finishedSpacingCellsRemaining: cellsRemaining,
                };
                break;
            case TaskName.HARVEST:
                updateParams = {
                    harvestAt: today,
                    isFinished: true,
                };
                break;
        }

        growthCycle.update(updateParams);
    }
}
